import { Injectable } from '@angular/core';

export interface Certificate {
  theme: string;
  issuer: string;
  serial: string;
  expiresAt?: string;
  fingerprint?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CertificateCacheService {
  private readonly CERTIFICATE_KEY = 'soar_certificate_cache';
  private readonly CERTIFICATE_EXPIRY_KEY = 'soar_certificate_expiry';
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas em millisegundos

  constructor() {}

  /**
   * Armazena um certificado no cache
   */
  storeCertificate(certificate: Certificate): void {
    try {
      const certificateData = {
        ...certificate,
        cachedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + this.CACHE_DURATION).toISOString()
      };

      localStorage.setItem(this.CERTIFICATE_KEY, JSON.stringify(certificateData));
      localStorage.setItem(this.CERTIFICATE_EXPIRY_KEY, certificateData.expiresAt);
      
      console.log('Certificado armazenado no cache:', certificateData);
    } catch (error) {
      console.error('Erro ao armazenar certificado:', error);
    }
  }

  /**
   * Recupera o certificado do cache se ainda for válido
   */
  getCachedCertificate(): Certificate | null {
    try {
      const certificateData = localStorage.getItem(this.CERTIFICATE_KEY);
      const expiryData = localStorage.getItem(this.CERTIFICATE_EXPIRY_KEY);

      if (!certificateData || !expiryData) {
        return null;
      }

      const certificate = JSON.parse(certificateData);
      const expiry = new Date(expiryData);

      // Verifica se o certificado ainda é válido
      if (new Date() > expiry) {
        this.clearCertificate();
        return null;
      }

      return certificate;
    } catch (error) {
      console.error('Erro ao recuperar certificado:', error);
      this.clearCertificate();
      return null;
    }
  }

  /**
   * Verifica se existe um certificado válido no cache
   */
  hasValidCertificate(): boolean {
    return this.getCachedCertificate() !== null;
  }

  /**
   * Limpa o certificado do cache
   */
  clearCertificate(): void {
    localStorage.removeItem(this.CERTIFICATE_KEY);
    localStorage.removeItem(this.CERTIFICATE_EXPIRY_KEY);
    console.log('Certificado removido do cache');
  }

  /**
   * Atualiza a data de expiração do certificado
   */
  refreshCertificateExpiry(): void {
    const certificate = this.getCachedCertificate();
    if (certificate) {
      this.storeCertificate(certificate);
    }
  }

  /**
   * Obtém informações sobre o cache
   */
  getCacheInfo(): { hasCertificate: boolean; expiresAt?: string; timeUntilExpiry?: number } {
    const certificate = this.getCachedCertificate();
    if (!certificate) {
      return { hasCertificate: false };
    }

    const expiry = new Date(certificate.expiresAt || '');
    const timeUntilExpiry = expiry.getTime() - Date.now();

    return {
      hasCertificate: true,
      expiresAt: certificate.expiresAt,
      timeUntilExpiry: timeUntilExpiry > 0 ? timeUntilExpiry : 0
    };
  }

  /**
   * Força a limpeza de certificados expirados
   */
  cleanupExpiredCertificates(): void {
    const certificate = this.getCachedCertificate();
    if (certificate && certificate.expiresAt) {
      const expiry = new Date(certificate.expiresAt);
      if (new Date() > expiry) {
        this.clearCertificate();
      }
    }
  }
}
